import { useCallback, useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { API, formatApiError } from "@/contexts/AuthContext";
import { PageHeader, PageBody, ErrorBlock } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  TrendingUp, RefreshCw, Hash, Music2, Video, Bookmark,
  Wand2, Sparkles, Search, Play, Heart, Eye, BookmarkPlus,
  AlertTriangle,
} from "lucide-react";

const REGIONS = ["US", "GB", "CA", "AU"];

function fmt(n) {
  if (n == null) return "—";
  if (n >= 1e9) return (n / 1e9).toFixed(1) + "B";
  if (n >= 1e6) return (n / 1e6).toFixed(1) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "K";
  return String(n);
}

function timeAgo(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return `${Math.round(s)}s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
}

export default function TrendsPage() {
  const [tab, setTab] = useState("hashtags"); // hashtags | sounds | videos | saved
  const [region, setRegion] = useState("US");
  const [data, setData] = useState({ hashtags: null, sounds: null, videos: null, saved: null });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryErr, setSummaryErr] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [activeHandle, setActiveHandle] = useState(() =>
    localStorage.getItem("ap-active-account") || "all"
  );
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/accounts`);
        if (!cancelled) setAccounts(r.data?.accounts || []);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, []);

  const activeAccount = useMemo(
    () => accounts.find((a) => a.tiktok_handle === activeHandle),
    [accounts, activeHandle]
  );
  const activeNicheTags = useMemo(
    () => activeAccount?.niche_tags || [],
    [activeAccount]
  );

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    setErrors({});
    const qs = `?region=${region}&limit=20${refresh ? "&refresh=true" : ""}`;
    const calls = [
      ["hashtags", `${API}/trends/tiktok/hashtags${qs}`],
      ["sounds", `${API}/trends/tiktok/sounds${qs}`],
      ["videos", `${API}/trends/tiktok/videos${qs}`],
      ["saved", `${API}/saved-trends`],
    ];
    const out = {};
    const errs = {};
    await Promise.all(
      calls.map(async ([k, url]) => {
        try {
          const r = await axios.get(url);
          out[k] = r.data;
        } catch (e) {
          errs[k] = e?.response?.data || { error: "network_error", detail: String(e) };
        }
      }),
    );
    setData(out);
    setErrors(errs);
    setLoading(false);
  }, [region]);

  const loadSummary = useCallback(async (refresh = false) => {
    setSummaryLoading(true);
    setSummaryErr(null);
    try {
      const params = new URLSearchParams();
      if (refresh) params.set("refresh", "true");
      if (activeNicheTags.length > 0) {
        params.set("niche_tags", activeNicheTags.join(","));
      }
      const qs = params.toString() ? `?${params.toString()}` : "";
      const r = await axios.get(`${API}/trends/tiktok/summary${qs}`);
      setSummary(r.data);
    } catch (e) {
      setSummaryErr(e?.response?.data || { error: "summary_failed", detail: String(e) });
    } finally {
      setSummaryLoading(false);
    }
  }, [activeNicheTags]);

  useEffect(() => { load(); loadSummary(); }, [load, loadSummary]);

  const applyAsNiche = (name, hashtag, productIdeas, presetIdea) => {
    nav("/app/generate", { state: {
      presetNiche: name, presetHashtag: hashtag,
      productIdeas: productIdeas || [], presetIdea: presetIdea || null,
    }});
  };

  const generateAllAngles = async (n) => {
    const ideas = (n.product_ideas || []).slice(0, 3);
    if (ideas.length === 0) return toast.error("No product ideas to batch.");
    if (!window.confirm(
      `Queue ${ideas.length} faceless videos for "${n.name}"?\n\n` +
      `Estimated cost: ~$0.03 total (LLM + Pexels + TTS).\n` +
      `All ${ideas.length} will appear in Library tagged with the same batch.`,
    )) return;
    try {
      const r = await axios.post(`${API}/video/batch`, {
        niche: n.name, hashtag: n.best_hashtag,
        product_ideas: ideas, style: "faceless",
      });
      toast.success(`Batch queued: ${r.data.jobs.length} videos rendering`);
      setTimeout(() => nav("/app/library"), 1200);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Batch failed");
    }
  };

  const saveTrend = async (kind, payload) => {
    try {
      await axios.post(`${API}/saved-trends`, { kind, payload });
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    }
  };

  const removeSaved = async (id) => {
    try {
      await axios.delete(`${API}/saved-trends/${id}`);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Remove failed");
    }
  };

  const doSearch = async (e) => {
    e?.preventDefault?.();
    if (!query.trim()) {
      setSearchResults(null);
      return;
    }
    try {
      const r = await axios.get(`${API}/trends/tiktok/search?q=${encodeURIComponent(query.trim())}&limit=20`);
      setSearchResults(r.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Search failed");
    }
  };

  // Client-side filter applied to all three tabs when query is non-empty.
  const q = query.trim().toLowerCase();
  const filt = (list, pred) => (q ? list.filter(pred) : list);
  const fHashtags = filt(data.hashtags?.items || [], (h) => (h.tag || "").toLowerCase().includes(q));
  const fSounds = filt(data.sounds?.items || [], (s) => (s.title || "").toLowerCase().includes(q) || (s.author || "").toLowerCase().includes(q));
  const baseVideos = searchResults?.items || data.videos?.items || [];
  const fVideos = filt(baseVideos, (v) => (v.caption || "").toLowerCase().includes(q) || (v.hashtags || []).some(t => t.toLowerCase().includes(q)));
  const counts = { hashtags: fHashtags.length, sounds: fSounds.length, videos: fVideos.length, saved: (data.saved || []).length };

  // Phase 3: detect RapidAPI monthly-quota exhaustion across any of the fetched errors
  // OR via the explicit upstream_warning flag the backend now surfaces, so we can show
  // an actionable banner instead of letting the user re-click Refresh.
  const quotaExhausted = (() => {
    // Backend surfaces this directly on the response payload when all seed
    // searches fail with the RapidAPI 429 quota message.
    for (const k of ["hashtags", "sounds", "videos"]) {
      if (data[k]?.upstream_warning === "rapidapi_monthly_quota_exhausted") return true;
    }
    const probe = (obj) => {
      if (!obj) return false;
      const s = JSON.stringify(obj).toLowerCase();
      return s.includes("monthly quota") || s.includes("you have exceeded") ||
             s.includes("rapidapi_monthly_quota_exhausted");
    };
    return probe(summaryErr) || Object.values(errors).some(probe);
  })();

  const meta = data[tab === "saved" ? "hashtags" : tab];
  const updated = meta?.fetched_at;

  return (
    <>
      <PageHeader
        icon={TrendingUp}
        title="TikTok Trends"
        subtitle="Live data via RapidAPI · 1h cache · AI summary refreshed every 2h"
        testid="trends-header"
        actions={
          <div className="flex items-center gap-2">
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger className="bg-zinc-900 border-zinc-700 text-zinc-100 w-20" data-testid="region-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100">
                {REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button
              onClick={() => load(true)}
              disabled={loading}
              variant="outline"
              size="sm"
              className="border-zinc-700 bg-zinc-900 hover:bg-zinc-800 text-zinc-100"
              data-testid="refresh-trends-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        }
      />

      <PageBody>
        {/* Phase 3: RapidAPI monthly-quota banner — surfaces when any upstream
            response carries the "MONTHLY quota" / "you have exceeded" error so
            operators know refreshes are paused until plan upgrade. */}
        {quotaExhausted && (
          <div
            className="ap-panel p-3 border-amber-700/60 bg-amber-500/[0.05] flex items-start gap-3"
            data-testid="rapidapi-quota-banner"
            role="alert"
          >
            <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-amber-200 font-semibold">
                Trend refresh paused — RapidAPI quota exhausted
              </div>
              <div className="text-[11px] text-amber-200/70 mt-0.5">
                The <span className="mono">tiktok-api23</span> Basic plan's monthly request quota is depleted.
                Cached trends are still served, but live refreshes (and the AI summary re-analysis)
                will fail until you upgrade the plan at{" "}
                <a
                  href="https://rapidapi.com/Lundehund/api/tiktok-api23"
                  target="_blank" rel="noreferrer"
                  className="underline hover:text-amber-100"
                >
                  rapidapi.com/Lundehund/api/tiktok-api23
                </a>.
              </div>
            </div>
          </div>
        )}

        {/* What's Hot Today AI summary */}
        <section className="ap-panel p-5" data-testid="summary-section">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <h2 className="text-sm font-semibold text-zinc-200 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-amber-400" />
              What's Hot Today
              {summary?.served_from && (
                <Badge variant="outline" className="border-zinc-700 mono text-[10px] text-zinc-500 ml-1">
                  {summary.served_from} · {timeAgo(summary.fetched_at)}
                </Badge>
              )}
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              {accounts.length > 0 && (
                <select
                  value={activeHandle}
                  onChange={(e) => {
                    setActiveHandle(e.target.value);
                    localStorage.setItem("ap-active-account", e.target.value);
                  }}
                  className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-xs text-zinc-200"
                  data-testid="trends-account-select"
                >
                  <option value="all">All niches (no filter)</option>
                  {accounts.map((a) => (
                    <option key={a.tiktok_handle} value={a.tiktok_handle}>
                      {`@${a.tiktok_handle} · ${(a.niche_tags || []).join("/")}`}
                    </option>
                  ))}
                </select>
              )}
              <Button onClick={() => loadSummary(true)} disabled={summaryLoading} size="sm" variant="outline"
                className="border-zinc-700 bg-zinc-900 text-zinc-200" data-testid="refresh-summary-btn">
                <RefreshCw className={`h-3.5 w-3.5 mr-1 ${summaryLoading ? "animate-spin" : ""}`} /> Re-analyze
              </Button>
            </div>
          </div>
          {activeNicheTags.length > 0 && (
            <div className="mb-3 text-[10px] mono text-zinc-500" data-testid="active-niche-tags">
              Filtering to: {activeNicheTags.map((t) => (
                <span key={t} className="inline-block mr-1 px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-700/40 text-emerald-300">
                  {t}
                </span>
              ))}
            </div>
          )}
          {summaryErr ? <ErrorBlock data={summaryErr} testid="summary-error" />
            : !summary ? <div className="text-xs text-zinc-500">Analyzing trends…</div>
            : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 md:overflow-visible overflow-x-auto md:overflow-x-visible" data-testid="summary-niches">
                {(summary.niches || []).map((n, i) => (
                  <div key={i} className="ap-panel-2 p-4 flex flex-col gap-2 min-w-[260px]" data-testid={`niche-card-${i}`}>
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-sm font-semibold text-emerald-300">{n.name}</h3>
                      <Badge variant="outline" className="border-zinc-700 mono text-[10px] text-zinc-500">
                        #{n.best_hashtag}
                      </Badge>
                    </div>
                    <p className="text-[11px] text-zinc-400 leading-relaxed">{n.why_hot}</p>
                    <div className="flex flex-wrap gap-1.5" data-testid={`niche-chips-${i}`}>
                      {(n.product_ideas || []).map((p, j) => (
                        <button
                          key={j}
                          onClick={() => applyAsNiche(n.name, n.best_hashtag, n.product_ideas, p)}
                          className="text-[10px] mono px-2 py-1 rounded-full border border-zinc-700 hover:border-emerald-500/60 hover:text-emerald-300 text-zinc-300 transition-colors"
                          data-testid={`niche-chip-${i}-${j}`}
                          title="Generate for this product"
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                    <Button
                      onClick={() => applyAsNiche(n.name, n.best_hashtag, n.product_ideas, null)}
                      size="sm"
                      className="mt-auto bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold text-xs"
                      data-testid={`niche-generate-${i}`}
                    >
                      <Wand2 className="h-3 w-3 mr-1" /> Generate (pick product)
                    </Button>
                    <Button
                      onClick={() => generateAllAngles(n)}
                      size="sm"
                      variant="outline"
                      className="border-amber-700/60 bg-amber-500/5 hover:bg-amber-500/10 text-amber-300 text-[10px] font-semibold"
                      data-testid={`niche-batch-${i}`}
                      title="Queue all 3 angles as faceless videos in parallel"
                    >
                      ⚡ Generate all 3 angles
                    </Button>
                  </div>
                ))}
              </div>
            )}
        </section>

        {/* Top 3 hashtags mini-row */}
        {data.hashtags?.items?.length > 0 && (
          <div className="ap-panel p-3 flex items-center gap-2 overflow-x-auto" data-testid="top3-hashtags">
            <span className="text-[10px] mono uppercase text-zinc-500 shrink-0">Top now</span>
            {data.hashtags.items.slice(0, 3).map((h, i) => (
              <button key={h.tag} onClick={() => applyAsNiche(h.tag, h.tag, [], null)} className="text-xs text-emerald-300 hover:text-emerald-200 shrink-0 mono" data-testid={`top3-tag-${i}`}>
                #{h.tag} <span className="text-zinc-500">· {fmt(h.view_count)}</span>
              </button>
            ))}
            <button onClick={() => setTab("hashtags")} className="ml-auto text-[11px] text-zinc-500 hover:text-zinc-300 shrink-0">see all →</button>
          </div>
        )}

        {/* Search */}
        <form onSubmit={doSearch} className="ap-panel p-3 flex items-center gap-2" data-testid="trends-search-form">
          <Search className="h-4 w-4 text-zinc-500 ml-2" />
          <Input
            value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search videos by keyword (e.g. wireless earbuds)…"
            className="flex-1 bg-transparent border-0 text-zinc-100 focus-visible:ring-0"
            data-testid="trends-search-input"
          />
          {searchResults && (
            <Button type="button" onClick={() => { setSearchResults(null); setQuery(""); }} variant="ghost" size="sm" className="text-zinc-400" data-testid="clear-search-btn">
              clear
            </Button>
          )}
          <Button type="submit" size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950" data-testid="trends-search-btn">
            Search
          </Button>
        </form>

        {/* Tabs */}
        <div className="ap-panel p-0 overflow-hidden">
          <div className="flex border-b border-zinc-800" data-testid="trends-tabs">
            {[
              { id: "hashtags", label: "Hashtags", icon: Hash, count: counts.hashtags },
              { id: "sounds", label: "Sounds", icon: Music2, count: counts.sounds },
              { id: "videos", label: "Videos", icon: Video, count: counts.videos },
              { id: "saved", label: "Saved", icon: Bookmark, count: counts.saved },
            ].map(({ id, label, icon: Icon, count }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                data-testid={`tab-${id}`}
                className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors ${
                  tab === id
                    ? "text-emerald-300 border-emerald-500 bg-emerald-500/5"
                    : "text-zinc-500 border-transparent hover:text-zinc-300"
                }`}
              >
                <Icon className="h-4 w-4" /> {label}
                {q && <span className="mono text-[10px] text-zinc-500">({count})</span>}
              </button>
            ))}
            <div className="ml-auto px-4 py-3 text-[11px] mono text-zinc-500 self-center">
              {updated && <>updated {timeAgo(updated)}</>}
            </div>
          </div>

          <div className="p-4">
            {q && (
              <div className="mb-3 text-[11px] text-zinc-400 flex items-center gap-2" data-testid="active-filter">
                Filtering by <span className="mono text-emerald-300">'{query}'</span>
                <button onClick={() => { setQuery(""); setSearchResults(null); }} className="text-zinc-500 hover:text-rose-400" data-testid="clear-filter-inline">✕ clear</button>
              </div>
            )}
            {tab === "hashtags" && (
              errors.hashtags ? <ErrorBlock data={errors.hashtags} testid="hashtags-error" />
                : !data.hashtags ? <Skel /> : (
                  <ul className="space-y-2" data-testid="hashtags-list">
                    {fHashtags.map((h, i) => (
                      <li key={h.tag} className="flex items-center justify-between gap-2 p-2 rounded-md hover:bg-zinc-900/60" data-testid={`hashtag-row-${i}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="mono text-xs text-zinc-500 w-6 text-right">{String(h.rank ?? i + 1).padStart(2, "0")}</span>
                          <a href={h.sample_video_url || "#"} target="_blank" rel="noreferrer" className="text-sm text-emerald-300 truncate hover:underline">#{h.tag}</a>
                        </div>
                        <div className="mono text-[11px] text-zinc-400 flex items-center gap-3 flex-wrap">
                          <span title="Posts in pool">{h.post_count} posts</span>
                          <span title="Aggregate views">{fmt(h.view_count)} views</span>
                          <button onClick={() => applyAsNiche(h.tag, h.tag, [], null)} className="inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300" data-testid={`use-niche-${i}`}>
                            <Wand2 className="h-3 w-3" /> use
                          </button>
                          <button onClick={() => saveTrend("hashtag", h)} className="inline-flex items-center gap-1 text-zinc-400 hover:text-amber-400" data-testid={`save-hashtag-${i}`} title="Save">
                            <BookmarkPlus className="h-3 w-3" />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )
            )}

            {tab === "sounds" && (
              errors.sounds ? <ErrorBlock data={errors.sounds} testid="sounds-error" />
                : !data.sounds ? <Skel /> : (
                  <ul className="space-y-2" data-testid="sounds-list">
                    {fSounds.map((s, i) => (
                      <li key={s.music_id || i} className="flex items-center justify-between gap-2 p-2 rounded-md hover:bg-zinc-900/60" data-testid={`sound-row-${i}`}>
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <span className="mono text-xs text-zinc-500 w-6 text-right">{String(s.rank ?? i + 1).padStart(2, "0")}</span>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm text-amber-200 truncate">{s.title || "(untitled)"}</div>
                            <div className="text-[11px] text-zinc-500 truncate">{s.author || "—"}</div>
                            {s.play_url && (
                              <audio controls preload="none" src={s.play_url} className="mt-1 h-7 w-full max-w-[260px]" data-testid={`sound-audio-${i}`} />
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mono text-[11px] text-zinc-500 shrink-0">
                          <span>{fmt(s.use_count)} reach</span>
                          {s.sample_video_url && (
                            <a href={s.sample_video_url} target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1" data-testid={`sound-watch-${i}`}>
                              <Play className="h-3 w-3" /> Watch original
                            </a>
                          )}
                          <button onClick={() => saveTrend("sound", s)} className="text-zinc-400 hover:text-amber-400" data-testid={`save-sound-${i}`} title="Save">
                            <BookmarkPlus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )
            )}

            {tab === "videos" && (
              <>
                {searchResults && (
                  <div className="text-xs text-zinc-400 mb-3" data-testid="search-results-header">
                    Search results for <span className="text-emerald-300">{searchResults.query}</span>
                  </div>
                )}
                {errors.videos && !searchResults ? <ErrorBlock data={errors.videos} testid="videos-error" />
                  : (fVideos.length === 0 ? <div className="text-xs text-zinc-500 text-center py-8">No matching videos.</div>
                  : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="videos-list">
                      {fVideos.map((v, i) => (
                        <a key={v.video_id || i} href={v.video_url || "#"} target="_blank" rel="noreferrer" className="ap-panel-2 overflow-hidden hover:border-emerald-700/50 transition-colors block" data-testid={`video-card-${i}`}>
                          <div className="aspect-[9/16] bg-black overflow-hidden relative">
                            {v.thumbnail_url ? (
                              <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-zinc-700"><Video className="h-8 w-8" /></div>
                            )}
                            <div className="absolute bottom-2 right-2 flex gap-2 text-white text-[10px] mono">
                              <span className="bg-black/60 rounded px-1.5 py-0.5 flex items-center gap-0.5"><Eye className="h-3 w-3" />{fmt(v.view_count)}</span>
                              <span className="bg-black/60 rounded px-1.5 py-0.5 flex items-center gap-0.5"><Heart className="h-3 w-3" />{fmt(v.like_count)}</span>
                            </div>
                          </div>
                          <div className="p-2 space-y-1.5">
                            <div className="text-[11px] text-zinc-400 truncate" title={v.author}>@{v.author || "?"}</div>
                            <div className="text-xs text-zinc-200 line-clamp-2 leading-tight">{v.caption || "(no caption)"}</div>
                            <div className="flex flex-wrap gap-1">
                              {(v.hashtags || []).slice(0, 3).map((t, j) => (
                                <button key={j} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setTab("hashtags"); }} className="text-[10px] mono text-emerald-300 hover:underline">#{t}</button>
                              ))}
                            </div>
                          </div>
                        </a>
                      ))}
                    </div>
                  ))}
              </>
            )}

            {tab === "saved" && (
              !data.saved ? <Skel /> : data.saved.length === 0
                ? <div className="text-sm text-zinc-500 text-center py-8" data-testid="saved-empty">No saved trends yet. Use the bookmark icons in other tabs.</div>
                : (
                  <ul className="space-y-2" data-testid="saved-list">
                    {data.saved.map((s) => (
                      <li key={s.id} className="flex items-center justify-between gap-2 p-2 rounded-md hover:bg-zinc-900/60" data-testid={`saved-row-${s.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <Badge variant="outline" className="border-zinc-700 mono text-[10px] text-zinc-400">{s.kind}</Badge>
                          <span className="text-sm text-zinc-200 truncate">
                            {s.kind === "hashtag" && `#${s.payload?.tag}`}
                            {s.kind === "sound" && (s.payload?.title || "(untitled)")}
                            {s.kind === "video" && (s.payload?.caption || s.payload?.video_id || "—")}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="mono text-[10px] text-zinc-500">{timeAgo(s.saved_at)}</span>
                          {s.kind === "hashtag" && (
                            <button onClick={() => applyAsNiche(s.payload.tag, s.payload.tag, [], null)} className="text-emerald-400 hover:text-emerald-300" data-testid={`saved-use-${s.id}`}>
                              <Wand2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <button onClick={() => removeSaved(s.id)} className="text-zinc-400 hover:text-rose-400" data-testid={`saved-remove-${s.id}`}>×</button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )
            )}
          </div>
        </div>
      </PageBody>
    </>
  );
}

function Skel() {
  return (
    <div className="space-y-2">
      {[...Array(6)].map((_, i) => <div key={i} className="h-9 rounded-md bg-zinc-900/60 animate-pulse" />)}
    </div>
  );
}
